import quantize from 'quantize'
import chroma from 'chroma-js'

/**
 * DominantColor Class
 *
 * @constructor
 */
export class DominantColor {
  constructor(el, options = {}) {
    this.parentEl = el
    this.w = 9
    this.h = 9
    this.arrayOfPixels = []
  }

  async init(imageSrc = '') {
    this.arrayOfPixels = []

    this.image = await this.loadImage(imageSrc).catch(error => {
      console.log('loadImage:error', error)
      return false
    })

    this.createHideCanvasRendorImage()

    return this.getDominantColor()
  }

  /**
   * Promiseを使用して画像読み込み
   *
   * @param {String} src - 画像ソース
   * @return {Promise}
   */
  loadImage(src = '') {
    return new Promise((resolve, reject) => {
      const img = new Image()
      img.onload = () => resolve(img)
      img.onerror = (e) => reject(e)
      img.src = src
    })
  }

  /**
   * 背景色が明るい範囲内か
   *
   * @param {Array} rgb - [r, b, a]
   * @return {String} - 'white' : 'black'
   */
  rbgCheckblackOrWhite(rgb = []) {
    const [r, g, b] = rgb
    const brightness = ((r * 299) + (g * 587) + (b * 114)) / 1000
    return (brightness < 130) ? 'white' : 'black'
  }

  createHideCanvasRendorImage() {
    this.hideCanvas = document.createElement('canvas')
    this.hideCanvas.width = this.w
    this.hideCanvas.height = this.h
    this.hideCtx = this.hideCanvas.getContext('2d')
    this.hideCtx.drawImage(this.image, 0, 0, this.w, this.h)
    this.pixel = this.hideCtx.getImageData(0, 0, this.w, this.h)

    if (!this.testCanvas) {
      this.testCanvas = document.createElement('canvas')
      this.testCanvas.className = 'testCanvas'
      this.testCanvas.width = this.w * 100
      this.testCanvas.height = this.h * 100
      this.testCanvas.style.width = `${this.w * 100}px`
      this.testCanvas.style.height = `${this.w * 100}px`
      this.parentEl.appendChild(this.testCanvas)
    }

    this.testCtx = this.testCanvas.getContext('2d')

    const onePixselSize = 100
    for (let x = 0; x < this.w; x++) {
      for (let y = 0; y < this.h; y++) {
        const index = ((x) + (y) * this.w) * 4
        const data = this.pixel.data
        const rgba = 'rgba(' + data[index + 0] + ',' + data[index + 1] + ',' + data[index + 2] + ', 1)'
        this.testCtx.fillStyle = rgba
        this.testCtx.fillRect((x * onePixselSize), (y * onePixselSize), onePixselSize, onePixselSize)
      }
    }
  }

  comparativeDistance(x, y) {
    /* See https://en.m.wikipedia.org/wiki/Euclidean_distance#Squared_Euclidean_distance */
    return (
      ((x[0] - y[0]) ** 2) +
      ((x[1] - y[1]) ** 2) +
      ((x[2] - y[2]) ** 2)
    )
  }

  /**
   * 投稿の画像から支配色を取得し設定する
   */
  getDominantColor() {
    console.log('----- New Color -----')

    // 画像のピクセルデータから1ピクセル毎の色を取得
    for (let x = 0; x < this.w; x++) {
      for (let y = 0; y < this.h; y++) {
        const index = ((x) + (y) * this.w) * 4
        const data = this.pixel.data
        const rgba = [data[index + 0], data[index + 1], data[index + 2], data[index + 3]]
        this.arrayOfPixels.push(rgba)
      }
    }

    let PRIMARY_RGB = false
    const filterMatchColor = this.arrayOfPixels.reduce((array, currentValue, currentIndex) => {
      array[currentValue] = (array[currentValue]) ? { count: ++array[currentValue].count, index: currentIndex } : { count: 1, index: currentIndex }
      return array
    }, {})
    const filterMatchMainColor = Object.values(filterMatchColor).reduce((a, b) => {
      return a.count > b.count ? a : b
    }, {})
    if (filterMatchMainColor && filterMatchMainColor.count >= (this.w + this.h)) {
      PRIMARY_RGB = this.arrayOfPixels[filterMatchMainColor.index]
    }

    const maximumColorCount = 2
    const colorMap = quantize(this.arrayOfPixels, maximumColorCount)
    const colors = colorMap.palette()

    if (!PRIMARY_RGB) {
      PRIMARY_RGB = colors[0]
    }

    let secondaryRGB = colors[2]

    const distance = this.comparativeDistance(PRIMARY_RGB, secondaryRGB)
    if (distance < 1000) {
      const textBlackOrWhite = this.rbgCheckblackOrWhite(PRIMARY_RGB)
      secondaryRGB = (textBlackOrWhite === 'black') ? [0, 0, 0] : [255, 255, 255]
    }

    // ピクセルと他の全てのピクセルとの色の差を取得する
    const primaryDeltaE = this.arrayOfPixels.map(color => {
      const deltaE = this.arrayOfPixels.reduce((acc, color2) => {
        return acc + chroma.deltaE(
          [color[0], color[1], color[2]],
          [color2[0], color2[1], color2[2]]
        )
      }, 0)
      return {
        deltaE: deltaE,
        color: color
      }
    })
    const primaryMinDelta = Math.min(...primaryDeltaE.map(c => c.deltaE))
    const primaryDeltaColor = primaryDeltaE.find(c => c.deltaE === primaryMinDelta)
    console.log('primaryDeltaColor', primaryDeltaColor)

    // テキスト色を取得する
    const textDeltaE = this.arrayOfPixels.map(color => {
      return {
        deltaE: chroma.deltaE(
          [primaryDeltaColor.color[0], primaryDeltaColor.color[1], primaryDeltaColor.color[2]],
          [color[0], color[1], color[2]]
        ),
        color: color
      }
    })
    const textDeltaEMax = Math.max(...textDeltaE.map((c) => c.deltaE))
    const textDeltaColor = textDeltaE.find(c => c.deltaE === textDeltaEMax)
    console.log('textDeltaColor', textDeltaColor)

    const contrast = chroma.contrast(
      [primaryDeltaColor.color[0], primaryDeltaColor.color[1], primaryDeltaColor.color[2]],
      [textDeltaColor.color[0], textDeltaColor.color[1], textDeltaColor.color[2]]
    )
    const luminance = chroma(
      [primaryDeltaColor.color[0], primaryDeltaColor.color[1], primaryDeltaColor.color[2]]
    ).luminance()

    console.log('contrast', contrast)
    console.log('luminance', luminance)

    if (contrast < 4) {
      if (luminance < 0.5) {
        const brighten = chroma([textDeltaColor.color[0], textDeltaColor.color[1], textDeltaColor.color[2]]).brighten(3).saturate().rgb()
        console.log('brighten', brighten)
        textDeltaColor.color = brighten
      } else {
        const darken = chroma([textDeltaColor.color[0], textDeltaColor.color[1], textDeltaColor.color[2]]).darken(3).saturate().rgb()
        console.log('darken', darken)
        textDeltaColor.color = darken
      }
    }

    const contrastLast = chroma.contrast(
      [primaryDeltaColor.color[0], primaryDeltaColor.color[1], primaryDeltaColor.color[2]],
      [textDeltaColor.color[0], textDeltaColor.color[1], textDeltaColor.color[2]]
    )
    const luminanceLast = chroma(
      [primaryDeltaColor.color[0], primaryDeltaColor.color[1], primaryDeltaColor.color[2]]
    ).luminance()

    console.log('contrastLast', contrastLast)
    console.log('luminanceLast', luminanceLast)

    return {
      hsl: `rgb(${primaryDeltaColor.color[0]}, ${primaryDeltaColor.color[1]}, ${primaryDeltaColor.color[2]})`,
      secondaryHsl: `rgb(${textDeltaColor.color[0]}, ${textDeltaColor.color[1]}, ${textDeltaColor.color[2]})`,
      hslText: this.rbgCheckblackOrWhite(PRIMARY_RGB),
      dominantColor: (PRIMARY_RGB) ? `rgba(${PRIMARY_RGB})` : `rgba(${colors[0]}, 1)`,
      gradientColor: `linear-gradient(160deg, rgba(${colors[0]}, 1), rgba(${colors[2]}, 1))`
    }
  }
}
